import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import * as moment from 'moment';

import { AuthenticationService } from '@app/_services';
import { User } from '@app/_models';
import { TaskService } from '@app/_components/task/task.service';
import { ReportService } from '@app/_components/report';

/**
 * Operations Dashboard Shell — v2 chrome wrapper (Option C).
 *
 * This component adds the v2 admin-nav-redesign chrome elements around
 * Dhruvit's existing <app-operations-dashboard> without modifying any of
 * his files. All additions are strictly outside his component boundary.
 *
 * Added elements (per Option C spec):
 *   1. Team-switcher color stripe (Operations = #1f4e79 deep blue)
 *   2. Greeting + quick-action buttons at top
 *   3. KPI action chips strip (Mine / Urgent / + New) — sits above the KPI tiles
 *   4. Universal "My Tasks" band — populated from TaskService.findByTaskListSummary
 *      using `type=me` (same backend path the task-home + task-list pages
 *      already use). Endpoint: GET /api/2.6/tasks/tasksummarylist/{userId}/me
 *   5. Surfaced folder cards row at bottom
 *   6. Status pills with one-click actions
 */
@Component({
    selector: 'app-operations-dashboard-shell',
    templateUrl: './operations-dashboard-shell.component.html',
    styleUrls: ['./operations-dashboard-shell.component.scss'],
    standalone: false,
})
export class OperationsDashboardShellComponent implements OnInit, OnDestroy {

    /** Time-of-day greeting suffix shown beside the page title. */
    greetingTime = '';

    /** My Tasks — populated from TaskService.findByTaskListSummary(...,'me'). */
    tasks: TaskSummary[] = [];
    tasksLoading = false;
    tasksUrgent = 0;
    tasksOpen = 0;

    /**
     * Live counts for the greeting band — fetched in parallel with Dhruvit's
     * component (which also calls operationsDashboardSection('kpis')). The
     * backend caches the result so the duplicate call is cheap. If the call
     * fails the greeting falls back to nav-only labels (no numeric).
     */
    kpis: OpsKpis | null = null;

    private currentUser: User | null = null;
    private subs: Subscription[] = [];

    constructor(
        private authenticationService: AuthenticationService,
        private taskService: TaskService,
        private reportService: ReportService,
    ) {}

    ngOnInit(): void {
        this.greetingTime = this.buildGreeting();
        this.loadGreetingKpis();

        // Subscribe to currentUser; reload tasks if the user changes mid-session.
        const userSub = this.authenticationService.currentUser.subscribe(user => {
            this.currentUser = user || null;
            if (this.currentUser && this.currentUser.userId) {
                this.loadTasks();
            }
        });
        this.subs.push(userSub);
    }

    /**
     * Fetch ONLY the 'kpis' section of the operations-dashboard endpoint so
     * the greeting band can show live numbers ("12 arrivals · 9 departures ·
     * 37 open WOs"). Independent of Dhruvit's component's own fetch — the
     * backend caches the section response so this is essentially free.
     * Fails silently — greeting band falls back to nav-only mode.
     */
    private loadGreetingKpis(): void {
        const sub = this.reportService.operationsDashboardSection('kpis').pipe(first()).subscribe({
            next: (res: any) => {
                const k = res && res.data && res.data.kpis ? res.data.kpis : null;
                if (k) {
                    this.kpis = {
                        arrivalsToday:   k.arrivalsToday   ?? null,
                        departuresToday: k.departuresToday ?? null,
                        openWorkOrders:  k.openWorkOrders  ?? null,
                    };
                }
            },
            error: () => { /* leave kpis null; template hides counts gracefully */ },
        });
        this.subs.push(sub);
    }

    /**
     * Format a numeric KPI for inline display. Returns '—' if null/undefined
     * so the greeting reads cleanly even before data lands.
     */
    fmtKpi(n: number | null | undefined): string {
        if (n === null || n === undefined) return '—';
        return String(n);
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }

    /** "good morning" / "good afternoon" / "good evening" based on local hour. */
    private buildGreeting(): string {
        const hour = moment().hour();
        if (hour < 12) return '· good morning';
        if (hour < 17) return '· good afternoon';
        return '· good evening';
    }

    /**
     * Fetch the current user's open tasks via the EXISTING TaskService endpoint
     * — same path the task-home page already uses (type='me'). Top 5 by priority,
     * descending. Fails gracefully — band shows empty state if the call errors.
     */
    private loadTasks(): void {
        if (!this.currentUser || !this.currentUser.userId) return;
        this.tasksLoading = true;

        // Args mirror the existing pattern in public-task-list / task-home:
        //   page=0, size=5, sortBy='priority' (sortOrder=-1 = desc), no multiSort,
        //   no globalFilter, empty filters, userId, type='me'.
        const sub = this.taskService.findByTaskListSummary(
            0, 5,
            'priority', -1,
            [], '', {},
            this.currentUser.userId,
            'me'
        ).pipe(first()).subscribe({
            next: (res: any) => {
                this.tasksLoading = false;
                const rawTasks = res && res.data && Array.isArray(res.data.tasks) ? res.data.tasks : [];
                this.tasks = rawTasks.map((t: any) => ({
                    taskId:       t.taskId || t.id,
                    priority:     this.normalizePriority(t.priority),
                    description:  t.subject || t.taskName || t.description || '(untitled)',
                    dueDateLabel: this.buildDueLabel(t.dueDate),
                }));
                this.tasksUrgent = this.tasks.filter(t => t.priority === 'URGENT').length;
                this.tasksOpen   = this.tasks.length;
            },
            error: () => {
                // Backend may be unavailable or the endpoint may fail — silently
                // hide rows rather than break the entire dashboard.
                this.tasksLoading = false;
            },
        });
        this.subs.push(sub);
    }

    /**
     * Normalize whatever priority value the backend sends into one of our 3
     * display buckets: URGENT / HIGH / NORMAL. Backend may send 1/2/3 integers
     * or string labels — we accept both.
     */
    private normalizePriority(p: any): string {
        if (p === 1 || p === '1' || (typeof p === 'string' && p.toUpperCase() === 'URGENT')) return 'URGENT';
        if (p === 2 || p === '2' || (typeof p === 'string' && p.toUpperCase() === 'HIGH'))   return 'HIGH';
        return 'NORMAL';
    }

    /**
     * Format a task due-date into a human label (e.g. "due today", "due in 2h",
     * "due Friday"). Returns empty string if no due date.
     * Date-safety per CLAUDE.md: use moment().startOf('day') for date-only math.
     */
    buildDueLabel(dueDate: string | null | undefined): string {
        if (!dueDate) return '';
        const due  = moment(dueDate).startOf('day');
        const now  = moment().startOf('day');
        const diff = due.diff(now, 'days');
        if (diff < 0)  return 'overdue';
        if (diff === 0) return 'due today';
        if (diff === 1) return 'due tomorrow';
        if (diff <= 6) return `due ${due.format('dddd')}`;
        return `due ${due.format('MMM D')}`;
    }

    /** CSS class applied to the priority badge based on priority value. */
    taskPrioClass(priority: string): string {
        switch ((priority || '').toUpperCase()) {
            case 'URGENT': return 'odsh-task-prio--urgent';
            case 'HIGH':   return 'odsh-task-prio--high';
            default:       return 'odsh-task-prio--normal';
        }
    }
}

interface TaskSummary {
    taskId:       number | string;
    priority:     string;
    description:  string;
    dueDateLabel: string;
}

interface OpsKpis {
    arrivalsToday:   number | null;
    departuresToday: number | null;
    openWorkOrders:  number | null;
}
